import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, CheckCircle, XCircle, X, Copy, History, Plus, ArrowDown, RefreshCw, Square } from "lucide-react";
import ReactMarkdown from "react-markdown";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ToastProvider";
import { useAgentChat } from "@/hooks/agent/useAgentChat";
import { useAgentTaskActions } from "@/hooks/useAgentTasksHitl";
import { agentUiBridge } from "@/lib/agentUiBridge";
import { useActiveAgentContext } from "@/context/ActiveAgentContext";
import { fetchThreads, type ObservabilityThread } from "@/api/observability";

// Stable component map for ReactMarkdown — defined outside component to avoid re-creation
const MD: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  p: ({ children }) => <p className="mb-1 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="my-1 list-disc space-y-0.5 pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="my-1 list-decimal space-y-0.5 pl-4">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  code: ({ children, className }) =>
    className ? (
      <pre className="my-1 overflow-x-auto rounded bg-muted p-2 text-[12px] font-mono">
        <code>{children}</code>
      </pre>
    ) : (
      <code className="rounded bg-muted px-1 py-0.5 text-[12px] font-mono">{children}</code>
    ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
      {children}
    </a>
  ),
  h1: ({ children }) => <h1 className="mb-1 mt-2 text-base font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-1 mt-2 text-sm font-bold">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 mt-2 text-sm font-semibold">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="my-1 border-l-2 border-border pl-3 text-muted-foreground">{children}</blockquote>
  ),
};

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {/* silent */});
}

function formatMsgTime(ts: string): string {
  const diff = Date.now() - Date.parse(ts);
  const minutes = Math.round(diff / 60000);
  if (minutes <= 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function isThoughtMessage(message: { type?: string | null; sender?: string | null }) {
  const type = (message.type ?? "").toLowerCase();
  const sender = (message.sender ?? "").toLowerCase();
  return type.includes("thought") || sender.includes("thought");
}

type AgentChatConsoleProps = {
  agentName: string;
  className?: string;
};

const ACTION_META_KEYS = new Set(["status", "action", "thread"]);

const getAgentDisplayName = (agentName: string): string => {
  const names: Record<string, string> = {
    booking: "BookingAgent",
    manager: "ManagerAgent",
    marketing: "MarketingAgent",
    publicist: "PublicistAgent",
    accounting: "AccountingAgent",
    podcast: "PodcastAgent",
    memory: "MemoryAgent",
    email: "EmailAgent",
    orchestrator: "OrchestratorAgent",
  };
  return names[agentName] || "Agent";
};

type QuickPrompt = { label: string; prompt: string };

const getQuickPromptsForAgent = (agentName: string): QuickPrompt[] => {
  switch (agentName) {
    case "booking":
      return [
        { label: "Pipeline summary", prompt: "Summarize my current booking pipeline and next actions." },
        { label: "New venue leads", prompt: "Propose new venue leads for the next 3 months using CRM + Bandsintown history." },
        { label: "Draft follow-ups", prompt: "Draft follow-ups for all Outreach leads that haven't replied." },
      ];
    case "manager":
      return [
        { label: "Team status", prompt: "Summarize status across all teams and highlight blockers." },
        { label: "Today's attention", prompt: "What tasks need my attention today?" },
        { label: "Stalled HITL", prompt: "Review stalled HITL items across agents." },
      ];
    case "marketing":
      return [
        { label: "Campaign ideas", prompt: "Suggest campaign ideas for upcoming shows." },
        { label: "Campaign review", prompt: "Review current campaign performance." },
        { label: "Draft social posts", prompt: "Draft social posts for next week." },
      ];
    case "publicist":
      return [
        { label: "Press release", prompt: "Draft a press release for the upcoming tour." },
        { label: "Coverage review", prompt: "Review recent press coverage and suggest follow-ups." },
        { label: "Find contacts", prompt: "Find media contacts for our next show." },
      ];
    case "email":
      return [
        { label: "Inbox priorities", prompt: "Summarize high-priority inbox threads and suggest replies." },
        { label: "Draft responses", prompt: "Draft responses for unread booking inquiries." },
        { label: "Follow-ups", prompt: "Identify follow-ups that need to be scheduled this week." },
      ];
    case "podcast":
      return [
        { label: "Find shows", prompt: "Find 5 podcast shows that would be a great fit for my artist and pitch the best one." },
        { label: "Review outreach", prompt: "Review my recent podcast pitches and flag any that need a follow-up." },
        { label: "Pitch strategy", prompt: "Build a podcast pitch strategy for my artist — what's the story angle and target show list?" },
      ];
    default:
      return [
        { label: "Status summary", prompt: "Summarize current status and next actions." },
        { label: "What needs attention?", prompt: "What needs my attention?" },
        { label: "Review tasks", prompt: "Review pending tasks." },
      ];
  }
};

/**
 * STEP 1: Get context-aware prompts based on active context
 * Returns 1-2 prompts that are specific to the currently focused entity
 */
const getContextualPrompts = (activeContext: { agent: string; kind: string; id: string } | null): QuickPrompt[] => {
  if (!activeContext) return [];

  const { kind, id } = activeContext;
  const kindLabel = kind.charAt(0).toUpperCase() + kind.slice(1);

  switch (kind) {
    case "lead":
      return [
        { label: "Summarize lead", prompt: `Summarize lead ${id} and suggest next action` },
        { label: "Draft follow-up", prompt: `Draft a follow-up email for lead ${id}` },
      ];
    case "task":
      return [
        { label: "Explain task", prompt: `Explain what task ${id} will do and its impact` },
        { label: "Task status", prompt: `Summarize the status of task ${id}` },
      ];
    case "goal":
      return [
        { label: "Goal summary", prompt: `Summarize goal ${id} and suggest next steps` },
        { label: "Goal blockers", prompt: `Identify blockers for goal ${id}` },
      ];
    case "venue":
    case "contact":
    case "event":
    case "campaign":
    case "release":
      return [
        { label: `Summarize ${kindLabel.toLowerCase()}`, prompt: `Summarize ${kindLabel.toLowerCase()} ${id} and recommend next action` },
      ];
    default:
      return [];
  }
};

const getAgentDescription = (agentName: string): string => {
  switch (agentName) {
    case "booking":
      return "Ask for routing advice, new leads, or follow-up drafts. High-risk actions remain HITL.";
    case "manager":
      return "Coordinate across teams, set goals, and review status. Delegations and sensitive actions require approval.";
    case "marketing":
      return "Plan campaigns, review analytics, and draft content. Campaign sends require approval.";
    case "publicist":
      return "Manage press relations, draft releases, and track coverage. Press sends require approval.";
    case "email":
      return "Draft replies, prioritize threads, and prepare follow-ups. Sends and escalations require approval.";
    case "podcast":
      return "Find podcast opportunities, craft targeted pitches, and track outreach. Email sends require approval.";
    default:
      return "Chat with the agent. High-risk actions require approval.";
  }
};

const getEmptyStateMessage = (agentName: string): string => {
  switch (agentName) {
    case "booking":
      return "Start by asking the Booking Agent to propose venues, draft outreach, or review Tavily candidates.";
    case "manager":
      return "Ask the Manager Agent to summarize status, coordinate teams, or review pending actions.";
    case "marketing":
      return "Ask the Marketing Agent to plan campaigns, review performance, or draft content.";
    case "publicist":
      return "Ask the Publicist Agent to draft press releases, find contacts, or track coverage.";
    case "email":
      return "Ask the Email Agent to summarize inbox priorities, draft replies, or schedule follow-ups.";
    case "podcast":
      return "Ask the Podcast Agent to find shows, build a pitch, or review your outreach history.";
    default:
      return "Start a conversation with the agent.";
  }
};

const extractActionInputs = (payload: Record<string, unknown>): Record<string, unknown> => {
  const candidate = payload.kwargs ?? payload.input ?? payload.args ?? payload.parameters;
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as Record<string, unknown>;
  }

  const filtered: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (!ACTION_META_KEYS.has(key)) {
      filtered[key] = value;
    }
  });
  return filtered;
};

const getActionToolName = (payload: Record<string, unknown>, content?: string | null): string => {
  const tool =
    (typeof payload.action === "string" && payload.action) ||
    (typeof payload.tool === "string" && payload.tool) ||
    (typeof payload.name === "string" && payload.name);
  if (tool) return tool;
  if (content) {
    const trimmed = content.trim();
    if (trimmed) return trimmed.split(/\s+/)[0];
  }
  return "agent.action";
};

export function AgentChatConsole({ agentName, className }: AgentChatConsoleProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { push } = useToast();
  const { threadId, messages, loading, error, sendMessage, stopThread, setThreadId, refresh } = useAgentChat(agentName);
  const { approveTask, rejectTask, isSubmitting } = useAgentTaskActions();
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [systemMessages, setSystemMessages] = useState<Array<{ id: string; text: string; timestamp: Date }>>([]);
  const [hiddenThoughtIds, setHiddenThoughtIds] = useState<Set<string>>(() => new Set());
  const [pendingSince, setPendingSince] = useState<number | null>(null);
  const [rejectingTaskId, setRejectingTaskId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [resolvedTaskIds, setResolvedTaskIds] = useState<Set<string>>(() => new Set());
  const [historyThreads, setHistoryThreads] = useState<ObservabilityThread[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentThreadMeta, setCurrentThreadMeta] = useState<ObservabilityThread | null>(null);
  const thoughtTimeoutsRef = useRef<Map<string, number>>(new Map());
  const systemTimeoutsRef = useRef<Map<string, number>>(new Map());
  const pendingTimeoutRef = useRef<number | null>(null);
  const sentMessageCountRef = useRef<number>(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const THOUGHT_TTL_MS = 8000;
  const SYSTEM_TTL_MS = 5000;

  // STEP 1: Active context support
  const { active, clearActive } = useActiveAgentContext();

  const agentDisplayName = getAgentDisplayName(agentName);
  const agentDescription = getAgentDescription(agentName);
  const emptyStateMessage = getEmptyStateMessage(agentName);

  // STEP 1: Merge contextual prompts with defaults
  const defaultPrompts = getQuickPromptsForAgent(agentName);
  const contextualPrompts = active && active.agent === agentName ? getContextualPrompts(active) : [];
  const quickPrompts = [...contextualPrompts, ...defaultPrompts];

  // STEP 1: Format context label
  const getContextLabel = (kind: string, id: string): string => {
    const kindLabel = kind.charAt(0).toUpperCase() + kind.slice(1);
    return `${kindLabel} • ${id}`;
  };

  // STEP 1: Check if context is for this agent
  const showContextPill = active && active.agent === agentName;

  // STEP 2: Build clipboard text for context copy
  const getContextClipboardText = (): string => {
    if (!active) return "";

    const kindLabel = active.kind.charAt(0).toUpperCase() + active.kind.slice(1);
    const lines: string[] = [
      `${kindLabel} Context`,
      `ID: ${active.id}`,
    ];

    // Add metadata if available from active.metadata
    if (active.metadata) {
      if (active.metadata.title) lines.push(`Title: ${active.metadata.title}`);
      if (active.metadata.venue) lines.push(`Venue: ${active.metadata.venue}`);
      if (active.metadata.status) lines.push(`Status: ${active.metadata.status}`);
      if (active.metadata.region) lines.push(`Region: ${active.metadata.region}`);
    }

    return lines.join("\n");
  };

  // STEP 2: Copy context to clipboard
  const handleCopyContext = () => {
    const text = getContextClipboardText();
    if (text) {
      navigator.clipboard.writeText(text).catch((err) => {
        console.warn("Failed to copy context to clipboard:", err);
      });
    }
  };

  // Subscribe to UI bridge events
  useEffect(() => {
    const unsubscribe = agentUiBridge.onAgent(agentName, (event) => {
      if (event.type === "chat.prefill") {
        setInput(event.text);
        if (event.focusChat && inputRef.current) {
          inputRef.current.focus();
        }
      } else if (event.type === "chat.appendSystemMessage") {
        setSystemMessages((prev) => [
          ...prev,
          {
            id: `system-${Date.now()}-${Math.random()}`,
            text: event.text,
            timestamp: new Date(),
          },
        ]);
      }
    });

    return unsubscribe;
  }, [agentName]);

  // Fetch thread metadata when threadId changes so we can show live/stopped status
  useEffect(() => {
    if (!threadId) { setCurrentThreadMeta(null); return; }
    let cancelled = false;
    fetchThreads().then((threads) => {
      if (!cancelled) setCurrentThreadMeta(threads.find((t) => t.id === threadId) ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [threadId]);

  // Internal statuses that should never appear in the user-facing chat.
  // These are agent reasoning internals: tool calls, observations, errors, etc.
  const INTERNAL_STATUSES = new Set([
    "proposed", "pending_hitl", "observed", "forbidden_action",
    "delegation_sent", "error", "message", "draft",
  ]);

  const orderedMessages = useMemo(
    () =>
      [...messages]
        // Keep: user context, agent responses, thoughts (auto-hidden), task proposals
        .filter((m) => {
          // Always show optimistic user messages
          if (m.optimistic) return true;
          const type = (m.type ?? "").toLowerCase();
          const status = (m.payload as Record<string, unknown> | null)?.status;
          // Show user messages (context) and agent responses unconditionally
          if (m.role === "user") return true;
          if (type === "response" || status === "response") return true;
          // Show thought notes (they auto-hide after 8s)
          if (isThoughtMessage(m)) return true;
          // Show task proposal observations (HITL flow)
          if (
            type === "observation" &&
            m.payload &&
            typeof m.payload === "object" &&
            "task_id" in m.payload
          ) return true;
          // Drop all other internal statuses
          if (typeof status === "string" && INTERNAL_STATUSES.has(status)) return false;
          // Drop raw action/observation notes without a task_id
          if (type === "action" || type === "observation") return false;
          // Default: show
          return true;
        })
        .sort((a, b) => {
          const left = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const right = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return left - right;
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  // Auto-handoff for orchestrator delegations in the hub UI.
  const handledDelegationsRef = useRef(new Set<string>());
  useEffect(() => {
    if (!messages.length) return;
    for (const message of messages) {
      const payload = message.payload as Record<string, unknown> | null | undefined;
      const targetAgent = payload?.delegation_to as string | undefined;
      if (!targetAgent || targetAgent === agentName) continue;
      if (handledDelegationsRef.current.has(message.id)) continue;
      handledDelegationsRef.current.add(message.id);

      const targetThread = payload?.target_thread_id as string | undefined;
      // Always switch to the target agent tab — even if ?agent= is not yet in the URL
      // (e.g. user navigated to /agents directly, which defaults to orchestrator without
      // adding ?agent= to the URL). AgentsHubPage reads from searchParams so setting
      // ?agent= here is always sufficient to trigger the tab switch.
      const next = new URLSearchParams(searchParams);
      next.set("agent", targetAgent);
      if (targetThread) next.set("thread", targetThread);
      else next.delete("thread");
      setSearchParams(next, { replace: true });
    }
  }, [messages, agentName, searchParams, setSearchParams]);

  // Track whether the user is scrolled to the bottom via the sentinel element
  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsAtBottom(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Scroll on new messages only if already at the bottom (don't yank user mid-read)
  useEffect(() => {
    if (isAtBottom) scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedMessages.length]);

  // Always scroll when the user sends (thinking indicator should be visible)
  useEffect(() => {
    if (pendingSince) scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSince]);

  // Auto-grow textarea to fit content
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 192)}px`;
  }, [input]);

  useEffect(() => {
    const now = Date.now();
    messages.forEach((message) => {
      if (!isThoughtMessage(message) || !message.id) return;
      if (hiddenThoughtIds.has(message.id) || thoughtTimeoutsRef.current.has(message.id)) return;
      const timestamp = message.timestamp ? new Date(message.timestamp).getTime() : now;
      const delay = Math.max(0, THOUGHT_TTL_MS - (now - timestamp));
      const timeoutId = window.setTimeout(() => {
        setHiddenThoughtIds((prev) => new Set(prev).add(message.id));
        thoughtTimeoutsRef.current.delete(message.id);
      }, delay);
      thoughtTimeoutsRef.current.set(message.id, timeoutId);
    });
  }, [messages, hiddenThoughtIds]);

  useEffect(() => {
    systemMessages.forEach((message) => {
      if (systemTimeoutsRef.current.has(message.id)) return;
      const timeoutId = window.setTimeout(() => {
        setSystemMessages((prev) => prev.filter((item) => item.id !== message.id));
        systemTimeoutsRef.current.delete(message.id);
      }, SYSTEM_TTL_MS);
      systemTimeoutsRef.current.set(message.id, timeoutId);
    });
  }, [systemMessages]);

  useEffect(() => {
    return () => {
      thoughtTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      thoughtTimeoutsRef.current.clear();
      systemTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      systemTimeoutsRef.current.clear();
      if (pendingTimeoutRef.current !== null) window.clearTimeout(pendingTimeoutRef.current);
    };
  }, []);

  // Clear processing indicator when new messages arrive after we sent
  useEffect(() => {
    if (pendingSince === null) return;
    if (messages.length > sentMessageCountRef.current) {
      setPendingSince(null);
      if (pendingTimeoutRef.current !== null) {
        window.clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    }
  }, [messages, pendingSince]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const all = await fetchThreads();
      const mine = all.filter(
        (t) =>
          t.agent.toLowerCase() === agentName.toLowerCase() ||
          t.agent.toLowerCase().includes(agentName.toLowerCase()),
      );
      // most recent first
      mine.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
      setHistoryThreads(mine.slice(0, 20));
    } finally {
      setHistoryLoading(false);
    }
  }, [agentName]);

  const resetConversationUiState = useCallback(() => {
    setSystemMessages([]);
    setHiddenThoughtIds(new Set());
    setRejectingTaskId(null);
    setRejectReason("");
    setResolvedTaskIds(new Set());
    setPendingSince(null);
    if (pendingTimeoutRef.current !== null) {
      window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, []);

  const handleNewConversation = useCallback(() => {
    resetConversationUiState();
    setThreadId(null);
  }, [resetConversationUiState, setThreadId]);

  const handleSwitchThread = useCallback(
    (id: string) => {
      resetConversationUiState();
      setThreadId(id);
    },
    [resetConversationUiState, setThreadId],
  );

  const handleSend = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setSending(true);
    try {
      sentMessageCountRef.current = messages.length;
      await sendMessage(content);
      setInput("");
      setPendingSince(Date.now());
      if (pendingTimeoutRef.current !== null) window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = window.setTimeout(() => {
        setPendingSince(null);
        pendingTimeoutRef.current = null;
      }, 90_000);
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to send message to ${agentDisplayName}.`;
      push({ title: "Send failed", message });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleStop = async () => {
    if (!threadId || stopping) return;
    setStopping(true);
    try {
      await stopThread(threadId);
      setPendingSince(null);
      if (pendingTimeoutRef.current !== null) {
        window.clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    } catch {
      // best-effort — UI clears pending state regardless
      setPendingSince(null);
    } finally {
      setStopping(false);
    }
  };

  const handleApproveInline = async (taskId: string, taskTitle: string) => {
    try {
      await approveTask(taskId);
      setResolvedTaskIds((prev) => new Set(prev).add(taskId));
      push({
        title: "Task approved",
        message: `Approved: ${taskTitle}. Autopilot will execute within ~60 seconds.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to approve task.";
      push({ title: "Approval failed", message });
    }
  };

  const handleRejectInline = (taskId: string) => {
    setRejectingTaskId(taskId);
    setRejectReason("");
  };

  const handleConfirmReject = async (taskId: string, taskTitle: string) => {
    try {
      await rejectTask(taskId, { reason: rejectReason.trim() || "Rejected from chat" });
      setResolvedTaskIds((prev) => new Set(prev).add(taskId));
      push({
        title: "Task rejected",
        message: `Rejected: ${taskTitle}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to reject task.";
      push({ title: "Rejection failed", message });
    } finally {
      setRejectingTaskId(null);
      setRejectReason("");
    }
  };

  const handleCancelReject = () => {
    setRejectingTaskId(null);
    setRejectReason("");
  };

  return (
    <Card className={`flex h-full flex-col border border-border/70 bg-background/80 ${className ?? ""}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">Chat with {agentDisplayName}</CardTitle>
              {currentThreadMeta?.active && (
                <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-400/40" variant="outline">
                  Live
                </Badge>
              )}
              {currentThreadMeta && !currentThreadMeta.active && (
                <Badge className="text-[10px]" variant="outline">
                  {currentThreadMeta.reason_for_stop === "max_turns_reached" ? "Max turns" :
                   currentThreadMeta.reason_for_stop === "error" ? "Errored" :
                   "Past session"}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{agentDescription}</p>
            {currentThreadMeta && currentThreadMeta.token_budget > 0 && (() => {
              const pct = Math.round((currentThreadMeta.tokens_used / currentThreadMeta.token_budget) * 100);
              if (pct < 70) return null;
              return (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-rose-500" : "bg-amber-500"}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <span className={`shrink-0 text-[11px] ${pct >= 90 ? "text-rose-400" : "text-amber-400"}`}>
                    {pct}% tokens
                  </span>
                </div>
              );
            })()}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button size="sm" variant="ghost" onClick={handleNewConversation} title="Start a new conversation">
              <Plus className="h-4 w-4" />
              <span className="sr-only">New chat</span>
            </Button>
            <Popover onOpenChange={(open) => { if (open) void loadHistory(); }}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" title="Browse conversation history">
                  <History className="h-4 w-4" />
                  <span className="sr-only">History</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-2">
                <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent conversations
                </p>
                {historyLoading ? (
                  <div className="space-y-1.5 px-2 py-1">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-3/4" />
                  </div>
                ) : historyThreads.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-muted-foreground">No past conversations found.</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {historyThreads.map((t) => {
                      const stopReason = t.reason_for_stop;
                      const tokenPct = t.token_budget > 0 ? Math.round((t.tokens_used / t.token_budget) * 100) : 0;
                      const stopColor =
                        stopReason === "error" ? "bg-rose-500" :
                        stopReason === "max_turns_reached" ? "bg-amber-500" : null;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => handleSwitchThread(t.id)}
                          className={`flex w-full flex-col gap-1 rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/50 ${
                            threadId === t.id ? "bg-muted font-semibold" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-foreground">
                              {new Date(t.start_time).toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </span>
                            <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                              <span>{t.turns} turns</span>
                              {t.active && (
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="Active" />
                              )}
                              {!t.active && stopColor && (
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${stopColor}`}
                                  title={stopReason ?? undefined}
                                />
                              )}
                            </span>
                          </div>
                          {t.token_budget > 0 && (
                            <div className="flex items-center gap-1.5">
                              <div className="h-0.5 flex-1 overflow-hidden rounded-full bg-muted">
                                <div
                                  className={`h-full rounded-full ${tokenPct > 80 ? "bg-amber-400" : "bg-emerald-500/60"}`}
                                  style={{ width: `${Math.min(tokenPct, 100)}%` }}
                                />
                              </div>
                              <span className="shrink-0 text-[10px] text-muted-foreground">{tokenPct}% tokens</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2">
          {quickPrompts.map(({ label, prompt }) => (
            <Button
              key={label}
              size="sm"
              variant="outline"
              title={prompt}
              onClick={() => void handleSend(prompt)}
              disabled={sending || !!pendingSince}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="relative flex-1 overflow-hidden rounded-xl border border-border/70 bg-background/60">
          <ScrollArea className="h-full p-4">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-16 w-11/12" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <p className="text-sm text-destructive">Unable to load conversation.</p>
                <Button size="sm" variant="outline" onClick={() => void refresh()}>
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                  Retry
                </Button>
              </div>
            ) : orderedMessages.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{emptyStateMessage}</p>
                {pendingSince && (
                  <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/40 px-3 py-2 text-sm">
                    <span className="flex gap-0.5 text-muted-foreground">
                      <span className="animate-bounce [animation-delay:0ms]">·</span>
                      <span className="animate-bounce [animation-delay:150ms]">·</span>
                      <span className="animate-bounce [animation-delay:300ms]">·</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{agentDisplayName} is thinking…</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* System messages (UI-only, not from backend) */}
                {systemMessages.map((sysMsg) => (
                  <div
                    key={sysMsg.id}
                    className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
                  >
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <Badge variant="warning">
                        System
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        {formatMsgTime(sysMsg.timestamp.toISOString())}
                      </span>
                    </div>
                    <p className="text-foreground">{sysMsg.text}</p>
                  </div>
                ))}

                {orderedMessages
                  .filter((message) => !hiddenThoughtIds.has(message.id) || !isThoughtMessage(message))
                  .map((message) => {
                  const sender = message.sender || "agent";
                  const isAgent = message.role === "agent";
                  const isActionMessage =
                    (message.type || "").toLowerCase() === "action" ||
                    (message.payload && typeof message.payload === "object" && "action" in message.payload);

                  // Check if this is a task proposal observation
                  const isTaskProposal = message.type === "observation" &&
                    message.payload &&
                    typeof message.payload === "object" &&
                    "task_id" in message.payload &&
                    "status" in message.payload &&
                    message.payload.status === "queued";

                  if (isTaskProposal) {
                    const payload = message.payload as Record<string, unknown>;
                    const taskId = payload.task_id as string;
                    const taskTitle = payload.task_title as string || "Agent task";
                    const taskType = payload.task_type as string;

                    return (
                      <div
                        key={message.id}
                        className="rounded-lg border border-info/50 bg-info/10 px-3 py-3 text-sm"
                      >
                        <div className="mb-2 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <Badge variant="info">
                              Task Created
                            </Badge>
                            {taskType && (
                              <span className="text-[11px] uppercase text-info">
                                {taskType.replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                          {message.timestamp && (
                            <span className="text-[11px] text-muted-foreground">
                              {formatMsgTime(message.timestamp)}
                            </span>
                          )}
                        </div>
                        <p className="mb-2 font-medium text-foreground">{taskTitle}</p>
                        <p className="mb-3 text-xs text-muted-foreground">
                          The agent created a task that requires your approval before execution.
                        </p>
                        {resolvedTaskIds.has(taskId) ? (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CheckCircle className="h-3.5 w-3.5 text-success" />
                            Actioned — agent will resume shortly
                          </div>
                        ) : rejectingTaskId === taskId ? (
                          <div className="space-y-2">
                            <input
                              autoFocus
                              type="text"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") void handleConfirmReject(taskId, taskTitle);
                                if (e.key === "Escape") handleCancelReject();
                              }}
                              placeholder="Reason for rejection (optional)"
                              className="w-full rounded-md border border-border bg-background/40 px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => void handleConfirmReject(taskId, taskTitle)}
                                disabled={isSubmitting}
                                className="h-7 text-xs"
                              >
                                <XCircle className="mr-1 h-3 w-3" />
                                Confirm reject
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleCancelReject}
                                disabled={isSubmitting}
                                className="h-7 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => void handleApproveInline(taskId, taskTitle)}
                              disabled={isSubmitting}
                              className="h-7 text-xs"
                            >
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRejectInline(taskId)}
                              disabled={isSubmitting}
                              className="h-7 text-xs"
                            >
                              <XCircle className="mr-1 h-3 w-3" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (isActionMessage) {
                    return (
                      <ActionMessageCard
                        key={message.id}
                        message={message}
                        isAgent={isAgent}
                      />
                    );
                  }

                  const msgText = message.content ?? JSON.stringify(message.payload);
                  return (
                    <div
                      key={message.id}
                      className={`group relative rounded-lg border px-3 py-2 text-sm ${
                        isAgent
                          ? "border-border/70 bg-background/40 text-foreground"
                          : "border-success/50 bg-success/10 text-foreground"
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{sender}</Badge>
                          {message.type && <span className="text-[11px] uppercase text-muted-foreground">{message.type}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          {message.timestamp && (
                            <span className="text-[11px] text-muted-foreground">
                              {formatMsgTime(message.timestamp)}
                            </span>
                          )}
                          <button
                            onClick={() => copyToClipboard(msgText ?? "")}
                            title="Copy message"
                            className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm">
                        <ReactMarkdown components={MD}>{msgText ?? ""}</ReactMarkdown>
                      </div>
                    </div>
                  );
                })}

                {pendingSince && (
                  <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/40 px-3 py-2 text-sm">
                    <span className="flex gap-0.5 text-muted-foreground">
                      <span className="animate-bounce [animation-delay:0ms]">·</span>
                      <span className="animate-bounce [animation-delay:150ms]">·</span>
                      <span className="animate-bounce [animation-delay:300ms]">·</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{agentDisplayName} is thinking…</span>
                  </div>
                )}
                {(() => {
                  const hiddenCount = orderedMessages.filter(
                    (m) => isThoughtMessage(m) && hiddenThoughtIds.has(m.id),
                  ).length;
                  return hiddenCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => setHiddenThoughtIds(new Set())}
                      className="w-full rounded-lg border border-dashed border-border/60 py-1.5 text-center text-[11px] text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                    >
                      {hiddenCount} thought{hiddenCount > 1 ? "s" : ""} hidden · Reveal
                    </button>
                  ) : null;
                })()}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
          {!isAtBottom && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full border border-border/70 bg-background/90 px-2.5 py-1 text-xs text-muted-foreground shadow-md backdrop-blur-sm transition-opacity hover:text-foreground"
            >
              <ArrowDown className="h-3 w-3" />
              {pendingSince ? "New message" : "Jump to bottom"}
            </button>
          )}
        </div>

        <div className="space-y-2">
          {/* STEP 1: Active Context Pill */}
          {showContextPill && (
            <div className="flex items-center gap-2 rounded-lg border border-info/50 bg-info/10 px-3 py-2">
              <Badge variant="secondary" className="text-xs">
                Context: {getContextLabel(active.kind, active.id)}
              </Badge>
              <div className="ml-auto flex items-center gap-2">
                {/* STEP 2: Copy Context action */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopyContext}
                  className="h-7 text-xs"
                >
                  <Copy className="mr-1 h-3 w-3" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearActive}
                  className="h-7 text-xs"
                >
                  <X className="h-3 w-3" />
                  Clear
                </Button>
              </div>
            </div>
          )}
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={`Ask the ${agentDisplayName} to help with your tasks…`}
            disabled={sending || stopping}
            className="resize-none"
            style={{ minHeight: "4.5rem", maxHeight: "12rem" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (pendingSince || currentThreadMeta?.active) {
                  void handleStop();
                } else {
                  void handleSend();
                }
              }
            }}
          />
          <div className="flex justify-end">
            {(() => {
              const isRunning = !!(pendingSince || currentThreadMeta?.active);
              return (
                <Button
                  onClick={() => void (isRunning ? handleStop() : handleSend())}
                  disabled={isRunning ? stopping : (sending || !input.trim())}
                  variant={isRunning ? "destructive" : "default"}
                  className="gap-2 transition-all"
                  title={isRunning ? "Stop the agent" : "Send message"}
                >
                  {isRunning ? (
                    <>
                      <Square className="h-4 w-4 fill-current" />
                      {stopping ? "Stopping…" : "Stop"}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      {sending ? "Sending…" : "Send"}
                    </>
                  )}
                </Button>
              );
            })()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type ActionMessageCardProps = {
  message: {
    sender: string;
    payload?: Record<string, unknown> | null;
    timestamp?: string | null;
    content?: string | null;
  };
  isAgent: boolean;
};

function ActionMessageCard({ message, isAgent }: ActionMessageCardProps) {
  const payload = (message.payload && typeof message.payload === "object") ? message.payload : {};
  const toolName = getActionToolName(payload, message.content);
  const inputs = extractActionInputs(payload);
  const title = typeof inputs.title === "string" ? inputs.title : null;
  const body = typeof inputs.body === "string" ? inputs.body : null;
  const otherFields = Object.entries(inputs).filter(([key]) => key !== "title" && key !== "body");
  const [expanded, setExpanded] = useState(false);

  const handleCopy = () => {
    const snapshot = {
      tool: toolName,
      inputs,
    };
    navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2)).catch((err) => {
      console.warn("Failed to copy action payload:", err);
    });
  };

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/5 p-4 text-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{message.sender || (isAgent ? "Agent" : "User")}</Badge>
          <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-amber-200">
            Action
          </span>
        </div>
        {message.timestamp && (
          <span className="text-[11px] text-muted-foreground">
            {formatMsgTime(message.timestamp)}
          </span>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tool call</p>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-foreground"
          onClick={handleCopy}
        >
          <Copy className="h-3 w-3" />
          Copy JSON
        </button>
      </div>

      <div className="rounded-lg border border-border/70 bg-background/60 px-3 py-2 text-xs text-foreground/80">
        <span className="font-mono">{toolName}</span>
      </div>

      <div className="mt-3 space-y-3">
        {title && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Title</p>
            <p className="text-sm font-semibold text-foreground">{title}</p>
          </div>
        )}
        {body && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Body</p>
            <p className={`text-sm text-foreground/80 whitespace-pre-line ${expanded ? "" : "line-clamp-4"}`}>
              {body}
            </p>
            <button
              type="button"
              className="mt-1 text-xs font-semibold text-primary hover:text-foreground"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          </div>
        )}
        {otherFields.length > 0 && (
          <div className="grid gap-2 md:grid-cols-2">
            {otherFields.map(([key, value]) => (
              <div key={key} className="rounded-lg border border-border/60 bg-background/40 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{key}</p>
                <p className="text-xs text-foreground/80 break-words">
                  {typeof value === "string" ? value : JSON.stringify(value)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
